import { useQuery } from "@tanstack/react-query";
import { usePublicClient } from "wagmi";
import { WrapperABI } from "@/contract/abi/wrapper";
import ContractAddress from "@/contract/addresses";
import { useEffectiveAddress } from "@/hooks/auth/useEffectiveAddress";

export type OwnedWrapper = {
  wrap_id: bigint;
  token_id: bigint;
  collection_address: `0x${string}`;
  plan_type: number; // 1..5
};

/**
 * Reads user's owned Wrapper NFTs.
 *
 * Matches old-front `useFetchUserStakeData` + `useNonStakeWrappers` pattern:
 *   1) `getUserTokens(user)` → returns wrap_id array
 *   2) Per-id `wrapTokenList(id)` multicall → returns plan + original token_id
 *
 * This is more robust than a single `getUserTokensInfo(user)` call because
 * the deployed contract may not expose the parameterized overload.
 *
 * Returns empty array when wallet not connected.
 */
export function useUserWrappers(): {
  data: OwnedWrapper[] | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const address = useEffectiveAddress();
  const publicClient = usePublicClient();

  const { data, isLoading, isError } = useQuery<OwnedWrapper[]>({
    queryKey: ["stake", "user-wrappers", address],
    staleTime: 0,
    enabled: !!address && !!publicClient,
    queryFn: async (): Promise<OwnedWrapper[]> => {
      if (!address || !publicClient) {
        return [];
      }
      // Step 1: user's wrap IDs
      const wrapIds = await publicClient.readContract({
        address: ContractAddress.WRAPPER,
        abi: WrapperABI,
        functionName: "getUserTokens",
        args: [address],
      });

      if (!wrapIds || wrapIds.length === 0) return [];

      // Step 2: per-id info via multicall
      const infos = await publicClient.multicall({
        contracts: wrapIds.map((id) => ({
          address: ContractAddress.WRAPPER,
          abi: WrapperABI,
          functionName: "wrapTokenList" as const,
          args: [id] as const,
        })),
        allowFailure: false,
      });

      const result = wrapIds
        .map((wrap_id, idx) => {
          const info = infos[idx];
          if (!info) return null;
          const [collection_address, token_id, plan_type, burned] = info;
          if (burned) return null;
          return {
            wrap_id,
            token_id,
            collection_address,
            plan_type: Number(plan_type),
          } satisfies OwnedWrapper;
        })
        .filter((w): w is OwnedWrapper => w !== null);

      return result;
    },
  });

  return { data, isLoading, isError };
}
